import { ElementProps } from '@/components/sections/builder/types';
import {
  ImageBannerElementDataType,
  ImageBannerTitleElementDataType,
} from '@/components/sections/builder/elements/body/image-banner/types';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { Type } from 'lucide-react';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';

const defaultData: ImageBannerTitleElementDataType = {
  name: 'Title',
  label: 'Browse our latest products',
  headingSize: 'small',
  padding: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ImageBannerTitleElementDataType;
  const parentData = element.parentData as ImageBannerElementDataType;

  return (
    <div className='w-full break-words md:max-w-[900px]'>
      <h1
        className={clsx(
          'duration-50 text_color_container theme_font_heading w-full font-extrabold',
          data.headingSize === 'large' && 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl',
          data.headingSize === 'medium' && 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl',
          data.headingSize === 'small' && 'text-lg sm:text-2xl md:text-3xl lg:text-4xl'
        )}
        data-animation='true'
        dangerouslySetInnerHTML={{ __html: data.label }}
      />
      <style jsx>
        {`
          @media (min-width: 1024px) {
            .title-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }

          .text_color_container {
            color: ${parentData.containerColorScheme?.text || '#6b7280'};
          }
        `}
      </style>
    </div>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ImageBannerTitleElementDataType;

  return (
    <div>
      <div className='mb-4 font-bold'>Text</div>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            label: value,
          })
        }
        value={data.label}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Heading Size</div>
      <InputTab
        defaultValue={data.headingSize}
        options={[
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            headingSize: v,
          })
        }
      />
    </div>
  );
}

const ImageBannerTitle = {
  type: 'image_banner_title',
  group: 'image_banner',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ImageBannerTitle;
